import { useMemo } from 'react';
import { useQuery } from '../hooks/useQuery';
import { useConfig, jiraIssueUrl } from '../hooks/useConfig';
import { useUrlParams } from '../hooks/useUrlState';
import { api, downloadFile, HygieneDeliverableRow, HygieneReport, HygieneUserRow, HygieneWorklogRow } from '../lib/api';
import {
  SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge,
  ActionButton, Spinner, EmptyState, MetricCard, SortableTh, UpdatingBadge,
} from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';

const fmtCurrencyShort = (n: number | string | null) => {
  if (n == null || n === '') return '—';
  const v = typeof n === 'string' ? parseFloat(n) : n;
  if (!isFinite(v) || v === 0) return '$0';
  return `$${Math.round(v)}/h`;
};

type Tab = 'deliverables' | 'users' | 'worklogs';
type WorklogBucketId = 'poor' | 'review';
type DelivBucketId =
  | 'all'
  | 'missing_delivery_manager'
  | 'missing_solution_architect'
  | 'missing_tempo_account'
  | 'zero_billable_rate'
  | 'completed_without_date';
type UserBucketId =
  | 'all'
  | 'directors_without_bonus_role'
  | 'missing_manager'
  | 'missing_team';

const DELIV_BUCKETS: Array<{ id: DelivBucketId; label: string; help: string; key?: keyof HygieneReport['deliverables'] }> = [
  { id: 'all',                         label: 'All issues',                    help: 'Every row flagged below' },
  { id: 'missing_delivery_manager',    label: 'Missing DM',                    help: "DM bonuses can't attribute", key: 'missing_delivery_manager' },
  { id: 'missing_solution_architect',  label: 'Missing SA',                    help: "SA bonuses can't attribute", key: 'missing_solution_architect' },
  { id: 'missing_tempo_account',       label: 'Missing Tempo account',         help: 'No rate → shipped value = 0', key: 'missing_tempo_account' },
  { id: 'zero_billable_rate',          label: 'Zero billable rate',            help: 'Account exists but no rate', key: 'zero_billable_rate' },
  { id: 'completed_without_date',      label: 'Completed w/o date',            help: "Won't roll up to any month", key: 'completed_without_date' },
];
const USER_BUCKETS: Array<{ id: UserBucketId; label: string; help: string; key?: keyof HygieneReport['users'] }> = [
  { id: 'all',                         label: 'All issues',                    help: 'Every user flagged below' },
  { id: 'directors_without_bonus_role', label: 'Dir/Exec — no bonus_role',     help: 'No personal bonus', key: 'directors_without_bonus_role' },
  { id: 'missing_manager',              label: 'Missing manager',              help: 'Breaks subtree rollup', key: 'missing_manager' },
  { id: 'missing_team',                 label: 'Missing team',                 help: 'Excluded from team filters', key: 'missing_team' },
];

const HYGIENE_DEFAULTS = { tab: 'deliverables', delivBucket: 'all', userBucket: 'all', worklogBucket: 'poor', search: '' };

export default function AdminHygienePage() {
  const { data, isLoading, isFetching, error, refetch } = useQuery(() => api.admin.hygiene());
  const [params, setParams] = useUrlParams(HYGIENE_DEFAULTS);
  const tab = params.tab as Tab;
  const delivBucket = params.delivBucket as DelivBucketId;
  const userBucket = params.userBucket as UserBucketId;
  const worklogBucket = params.worklogBucket as WorklogBucketId;
  const search = params.search;
  const setTab           = (v: Tab)             => setParams({ tab: v });
  const setDelivBucket   = (v: DelivBucketId)   => setParams({ delivBucket: v });
  const setUserBucket    = (v: UserBucketId)    => setParams({ userBucket: v });
  const setWorklogBucket = (v: WorklogBucketId) => setParams({ worklogBucket: v });
  const setSearch        = (v: string)          => setParams({ search: v });

  if (isLoading) return <Spinner />;
  if (error) return <EmptyState message={error} />;
  if (!data) return null;

  const h: HygieneReport = data;

  const delivCounts = {
    missing_delivery_manager: h.deliverables.missing_delivery_manager.length,
    missing_solution_architect: h.deliverables.missing_solution_architect.length,
    missing_tempo_account: h.deliverables.missing_tempo_account.length,
    zero_billable_rate: h.deliverables.zero_billable_rate.length,
    completed_without_date: h.deliverables.completed_without_date.length,
  };
  const userCounts = {
    directors_without_bonus_role: h.users.directors_without_bonus_role.length,
    missing_manager: h.users.missing_manager.length,
    missing_team: h.users.missing_team.length,
  };
  const totalDeliv = Object.values(delivCounts).reduce((s, n) => s + n, 0);
  const totalUsers = Object.values(userCounts).reduce((s, n) => s + n, 0);
  const worklogCounts = {
    poor:   h.worklogs?.poor_descriptions?.length   ?? 0,
    review: h.worklogs?.review_descriptions?.length ?? 0,
  };
  const totalWorklogs = worklogCounts.poor + worklogCounts.review;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Data hygiene</SectionLabel>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {isFetching && <UpdatingBadge />}
          <ActionButton onClick={() => setParams(HYGIENE_DEFAULTS)} variant="default">Clear filters</ActionButton>
          <ActionButton onClick={() => downloadFile('/admin/hygiene?format=csv', 'hygiene-report.csv')} variant="default">Export CSV</ActionButton>
          <ActionButton onClick={refetch} variant="default">Refresh</ActionButton>
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 10 }}>
        <MetricCard
          label="Total issues"
          value={totalDeliv + totalUsers + totalWorklogs}
          accent={(totalDeliv + totalUsers + totalWorklogs) === 0 ? 'green' : 'amber'}
          sub={(totalDeliv + totalUsers + totalWorklogs) === 0 ? 'All clear' : 'Deliverables + users + worklogs'}
        />
        <MetricCard label="Deliverable issues" value={totalDeliv} accent={totalDeliv === 0 ? 'green' : 'amber'} sub="Fix in Jira" />
        <MetricCard label="User issues"        value={totalUsers} accent={totalUsers === 0 ? 'green' : 'amber'} sub="Fix in Users & roles" />
        <MetricCard label="Worklog descriptions" value={totalWorklogs} accent={totalWorklogs === 0 ? 'green' : 'amber'} sub={`${worklogCounts.poor} poor · ${worklogCounts.review} review`} />
      </div>

      <Card>
        <CardHeader>
          <TabButton active={tab === 'deliverables'} onClick={() => setTab('deliverables')}>
            Deliverables <Badge variant={totalDeliv === 0 ? 'green' : 'amber'}>{totalDeliv}</Badge>
          </TabButton>
          <TabButton active={tab === 'users'} onClick={() => setTab('users')}>
            Users <Badge variant={totalUsers === 0 ? 'green' : 'amber'}>{totalUsers}</Badge>
          </TabButton>
          <TabButton active={tab === 'worklogs'} onClick={() => setTab('worklogs')}>
            Worklogs <Badge variant={totalWorklogs === 0 ? 'green' : 'amber'}>{totalWorklogs}</Badge>
          </TabButton>
          <span style={{ marginLeft: 'auto' }}>
            <input
              type="search"
              placeholder="Filter…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                fontSize: 11, padding: '4px 8px', width: 220,
                border: '0.5px solid var(--color-border-strong)',
                borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)',
              }}
            />
          </span>
        </CardHeader>

        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border)', background: 'var(--color-bg)' }}>
          {tab === 'deliverables' && DELIV_BUCKETS.map((b) => {
            const count = b.id === 'all' ? totalDeliv : (delivCounts[b.id as keyof typeof delivCounts] ?? 0);
            return (
              <BucketChip
                key={b.id} active={delivBucket === b.id}
                onClick={() => setDelivBucket(b.id)} label={b.label} count={count} help={b.help}
              />
            );
          })}
          {tab === 'users' && USER_BUCKETS.map((b) => {
            const count = b.id === 'all' ? totalUsers : (userCounts[b.id as keyof typeof userCounts] ?? 0);
            return (
              <BucketChip
                key={b.id} active={userBucket === b.id}
                onClick={() => setUserBucket(b.id)} label={b.label} count={count} help={b.help}
              />
            );
          })}
          {tab === 'worklogs' && ([
            { id: 'poor'   as WorklogBucketId, label: 'Poor',   help: 'Unacceptable customer-facing descriptions', count: worklogCounts.poor   },
            { id: 'review' as WorklogBucketId, label: 'Review', help: 'Borderline — human to judge',                count: worklogCounts.review },
          ]).map((b) => (
            <BucketChip
              key={b.id} active={worklogBucket === b.id}
              onClick={() => setWorklogBucket(b.id)} label={b.label} count={b.count} help={b.help}
            />
          ))}
        </div>

        {tab === 'deliverables' && <DeliverableTable h={h} bucket={delivBucket} search={search} />}
        {tab === 'users'        && <UserTable       h={h} bucket={userBucket}  search={search} />}
        {tab === 'worklogs'     && <WorklogTable   h={h} bucket={worklogBucket} search={search} />}
      </Card>
    </>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-blue)' : '2px solid transparent',
        padding: '8px 16px',
        fontSize: 12,
        fontWeight: 500,
        color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
        cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      {children}
    </button>
  );
}

function BucketChip({
  active, onClick, label, count, help,
}: { active: boolean; onClick: () => void; label: string; count: number; help: string }) {
  return (
    <button
      onClick={onClick}
      title={help}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11,
        padding: '4px 10px',
        borderRadius: 4,
        border: active ? '0.5px solid var(--color-blue)' : '0.5px solid var(--color-border-strong)',
        background: active ? 'var(--color-blue-bg)' : 'var(--color-surface)',
        color: active ? 'var(--color-blue-text)' : 'var(--color-text-muted)',
        cursor: 'pointer',
      }}
    >
      {label}
      <Badge variant={active ? 'blue' : count === 0 ? 'green' : 'amber'}>{count}</Badge>
    </button>
  );
}

// ─── Deliverable table ──────────────────────────────────────────────────────

function DeliverableTable({ h, bucket, search }: { h: HygieneReport; bucket: DelivBucketId; search: string }) {
  const { config } = useConfig();
  const rows = useMemo(() => {
    const collect: Array<{ row: HygieneDeliverableRow; bucketLabel: string }> = [];
    const tag = (label: string, list: HygieneDeliverableRow[]) => list.forEach((r) => collect.push({ row: r, bucketLabel: label }));
    if (bucket === 'all' || bucket === 'missing_delivery_manager')   tag('Missing DM',            h.deliverables.missing_delivery_manager);
    if (bucket === 'all' || bucket === 'missing_solution_architect') tag('Missing SA',            h.deliverables.missing_solution_architect);
    if (bucket === 'all' || bucket === 'missing_tempo_account')      tag('Missing Tempo account', h.deliverables.missing_tempo_account);
    if (bucket === 'all' || bucket === 'zero_billable_rate')         tag('Zero rate',             h.deliverables.zero_billable_rate);
    if (bucket === 'all' || bucket === 'completed_without_date')     tag('Completed w/o date',    h.deliverables.completed_without_date);

    const needle = search.trim().toLowerCase();
    if (!needle) return collect;
    return collect.filter(({ row }) =>
      row.jira_issue_key.toLowerCase().includes(needle)
      || (row.summary ?? '').toLowerCase().includes(needle)
      || (row.status ?? '').toLowerCase().includes(needle),
    );
  }, [h, bucket, search]);

  const { sorted, sort } = useTableSort(
    rows,
    {
      issue:   ({ row }) => row.jira_issue_key,
      status:  ({ row }) => row.status,
      bucket:  ({ bucketLabel }) => bucketLabel,
      rate:    ({ row }) => Number(row.billable_rate ?? 0),
      account: ({ row }) => row.tempo_account_id ?? '',
    },
    { key: 'issue', dir: 'asc' },
  );

  if (sorted.length === 0) {
    return <EmptyState message={search ? 'No matches.' : 'All clear.'} />;
  }

  return (
    <div style={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
      <Table>
        <thead>
          <tr>
            <SortableTh columnKey="issue"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Issue</SortableTh>
            <SortableTh columnKey="status"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Status</SortableTh>
            <SortableTh columnKey="bucket"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Bucket</SortableTh>
            <SortableTh columnKey="rate"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Rate</SortableTh>
            <SortableTh columnKey="account" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Account</SortableTh>
            <Th>Jira</Th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(({ row, bucketLabel }) => {
            const url = jiraIssueUrl(config, row.jira_issue_key);
            return (
              <Tr key={`${bucketLabel}-${row.jira_issue_id}`}>
                <Td bold>
                  <span style={{ marginRight: 6 }}>{row.jira_issue_key}</span>
                  <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{row.summary}</span>
                </Td>
                <Td><Badge variant="gray">{row.status}</Badge></Td>
                <Td muted style={{ fontSize: 11 }}><Badge variant="amber">{bucketLabel}</Badge></Td>
                <Td right muted>{fmtCurrencyShort(row.billable_rate)}</Td>
                <Td muted style={{ fontSize: 11 }}>{row.tempo_account_id ?? '—'}</Td>
                <Td>{url ? <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-blue)', fontSize: 11 }}>Open ↗</a> : '—'}</Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

// ─── Worklog-description table ──────────────────────────────────────────────

function WorklogTable({ h, bucket, search }: { h: HygieneReport; bucket: WorklogBucketId; search: string }) {
  const { config } = useConfig();
  const rows = useMemo(() => {
    const src: HygieneWorklogRow[] = bucket === 'poor'
      ? (h.worklogs?.poor_descriptions ?? [])
      : (h.worklogs?.review_descriptions ?? []);
    const needle = search.trim().toLowerCase();
    if (!needle) return src;
    return src.filter((r) =>
      (r.description ?? '').toLowerCase().includes(needle)
      || (r.deliverable_key ?? '').toLowerCase().includes(needle)
      || (r.author_display_name ?? '').toLowerCase().includes(needle),
    );
  }, [h, bucket, search]);

  const { sorted, sort } = useTableSort(
    rows,
    {
      score:     (r) => r.score,
      date:      (r) => r.work_date,
      hours:     (r) => Number(r.logged_hours),
      author:    (r) => r.author_display_name ?? '',
      issue:     (r) => r.deliverable_key ?? '',
    },
    { key: 'score', dir: 'asc' },
  );

  if (sorted.length === 0) {
    return <EmptyState message={search ? 'No matches.' : 'All clear.'} />;
  }

  return (
    <div style={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
      <Table>
        <thead>
          <tr>
            <SortableTh columnKey="date"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Date</SortableTh>
            <SortableTh columnKey="author" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Author</SortableTh>
            <SortableTh columnKey="issue"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Deliverable</SortableTh>
            <SortableTh columnKey="hours"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Hours</SortableTh>
            <Th>Description</Th>
            <Th>Reasons</Th>
            <SortableTh columnKey="score"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Score</SortableTh>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const url = r.deliverable_key ? jiraIssueUrl(config, r.deliverable_key) : null;
            return (
              <Tr key={r.tempo_worklog_id}>
                <Td muted style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{r.work_date}</Td>
                <Td muted style={{ fontSize: 11 }}>{r.author_display_name ?? '—'}</Td>
                <Td style={{ fontSize: 11 }}>
                  {url
                    ? <a href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-blue)' }}>{r.deliverable_key}</a>
                    : (r.deliverable_key ?? '—')}
                  <div style={{ color: 'var(--color-text-muted)', fontSize: 10 }}>{r.deliverable_summary ?? ''}</div>
                </Td>
                <Td right muted>{Number(r.logged_hours).toFixed(2)}</Td>
                <Td style={{ fontSize: 11, maxWidth: 360, whiteSpace: 'normal' }}>
                  {r.description ? `"${r.description}"` : <span style={{ color: 'var(--color-text-muted)' }}>(empty)</span>}
                </Td>
                <Td style={{ fontSize: 10, maxWidth: 280, whiteSpace: 'normal' }} muted>
                  {r.reasons.join('; ')}
                </Td>
                <Td right bold style={{ color: r.bucket === 'poor' ? 'var(--color-red-text)' : 'var(--color-amber-text, var(--color-text))' }}>
                  {r.score}
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

// ─── User table ─────────────────────────────────────────────────────────────

function UserTable({ h, bucket, search }: { h: HygieneReport; bucket: UserBucketId; search: string }) {
  const rows = useMemo(() => {
    const collect: Array<{ row: HygieneUserRow; bucketLabel: string }> = [];
    const tag = (label: string, list: HygieneUserRow[]) => list.forEach((r) => collect.push({ row: r, bucketLabel: label }));
    if (bucket === 'all' || bucket === 'directors_without_bonus_role') tag('No bonus_role', h.users.directors_without_bonus_role);
    if (bucket === 'all' || bucket === 'missing_manager')               tag('No manager',   h.users.missing_manager);
    if (bucket === 'all' || bucket === 'missing_team')                  tag('No team',      h.users.missing_team);

    const needle = search.trim().toLowerCase();
    if (!needle) return collect;
    return collect.filter(({ row }) =>
      row.display_name.toLowerCase().includes(needle)
      || (row.email ?? '').toLowerCase().includes(needle)
      || (row.team ?? '').toLowerCase().includes(needle),
    );
  }, [h, bucket, search]);

  const { sorted, sort } = useTableSort(
    rows,
    {
      name:   ({ row }) => row.display_name,
      email:  ({ row }) => row.email ?? '',
      role:   ({ row }) => row.role,
      title:  ({ row }) => row.title ?? '',
      team:   ({ row }) => row.team ?? '',
      bucket: ({ bucketLabel }) => bucketLabel,
    },
    { key: 'name', dir: 'asc' },
  );

  if (sorted.length === 0) {
    return <EmptyState message={search ? 'No matches.' : 'All clear.'} />;
  }

  return (
    <div style={{ maxHeight: 'calc(100vh - 340px)', overflow: 'auto' }}>
      <Table>
        <thead>
          <tr>
            <SortableTh columnKey="name"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Name</SortableTh>
            <SortableTh columnKey="email"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Email</SortableTh>
            <SortableTh columnKey="role"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Role</SortableTh>
            <SortableTh columnKey="title"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Title</SortableTh>
            <SortableTh columnKey="team"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Team</SortableTh>
            <SortableTh columnKey="bucket" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Bucket</SortableTh>
          </tr>
        </thead>
        <tbody>
          {sorted.map(({ row, bucketLabel }) => (
            <Tr key={`${bucketLabel}-${row.jira_user_id}`}>
              <Td bold>{row.display_name}</Td>
              <Td muted style={{ fontSize: 11 }}>{row.email || '—'}</Td>
              <Td><Badge variant="gray">{row.role}</Badge></Td>
              <Td muted style={{ fontSize: 11 }}>{row.title || '—'}</Td>
              <Td muted style={{ fontSize: 11 }}>{row.team || '—'}</Td>
              <Td muted style={{ fontSize: 11 }}><Badge variant="amber">{bucketLabel}</Badge></Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
